import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  DialogContent,
  DialogTitle,
  Modal,
  ModalDialog,
  Sheet,
  Stack,
  Table,
  Typography,
} from "@mui/joy";
import {
  DashboardColumn,
  DashboardDrilldownDefinition,
  DashboardDrilldownResponse,
  DashboardSort,
} from "../types";
import { useOrganizationDashboardsApi } from "../api";
import { useDashboardContext } from "../DashboardContext";

interface Props {
  open: boolean;
  onClose: () => void;
  organizationId: string;
  moduleId: string;
  panelId: string;
  title: string;
  definition?: DashboardDrilldownDefinition | null;
}
export const DrilldownDialog: React.FC<Props> = ({
  open,
  onClose,
  organizationId,
  moduleId,
  panelId,
  title,
  definition,
}) => {
  const { getDrilldown, downloadCsv } = useOrganizationDashboardsApi();
  const { range, refreshVersion } = useDashboardContext();
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<DashboardSort>(
    definition?.default_sort ?? { key: "id", direction: "asc" },
  );
  const [data, setData] = useState<DashboardDrilldownResponse | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [retryVersion, setRetryVersion] = useState(0);
  const pageSize = definition?.page_size ?? 25;
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError("");
    getDrilldown(organizationId, moduleId, panelId, range, page, pageSize, sort)
      .then((result) => {
        if (!cancelled) setData(result);
      })
      .catch((reason: unknown) => {
        if (!cancelled)
          setError(
            reason instanceof Error
              ? reason.message
              : "Could not load detail rows.",
          );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    open,
    organizationId,
    moduleId,
    panelId,
    range,
    page,
    pageSize,
    sort,
    refreshVersion,
    retryVersion,
    getDrilldown,
  ]);
  const download = async () => {
    if (!data?.csv_export?.href) return;
    try {
      const blob = await downloadCsv(data.csv_export.href);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${moduleId}-${panelId}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : "CSV download failed.",
      );
    }
  };
  const toggleSort = (column: DashboardColumn) => {
    setSort((current) => ({
      key: column.key,
      direction:
        current.key === column.key && current.direction === "asc"
          ? "desc"
          : "asc",
    }));
    setPage(1);
  };
  const totalRows = data?.total_rows ?? 0;
  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog
        sx={{ width: "min(1000px, 96vw)", maxHeight: "90vh", overflow: "auto" }}
      >
        <DialogTitle>{title} details</DialogTitle>
        <DialogContent>Rows for the selected time range</DialogContent>
        {error && (
          <Alert color="danger" role="alert">
            {error}
            <Button
              size="sm"
              variant="plain"
              onClick={() => setRetryVersion((value) => value + 1)}
            >
              Retry
            </Button>
          </Alert>
        )}
        {loading ? (
          <Typography role="status">Loading detail rows…</Typography>
        ) : (
          data && (
            <>
              <Sheet
                variant="outlined"
                sx={{ overflow: "auto", borderRadius: "sm" }}
              >
                <Table size="sm" stickyHeader>
                  <thead>
                    <tr>
                      {data.columns.map((column) => (
                        <th key={column.key}>
                          <button
                            type="button"
                            onClick={() => toggleSort(column)}
                          >
                            {column.label}
                            {sort.key === column.key
                              ? sort.direction === "asc"
                                ? " ▲"
                                : " ▼"
                              : ""}
                          </button>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.rows.map((row, index) => (
                      <tr key={String(row.id ?? index)}>
                        {data.columns.map((column) => (
                          <td key={column.key}>
                            {String(row[column.key] ?? "—")}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Sheet>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
              >
                <Typography level="body-sm">
                  {totalRows} rows · Page {data.page}
                </Typography>
                <Stack direction="row" spacing={1}>
                  {data.csv_export?.href && (
                    <Button size="sm" variant="outlined" onClick={download}>
                      Download CSV
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="outlined"
                    disabled={page <= 1}
                    onClick={() => setPage((value) => Math.max(1, value - 1))}
                  >
                    Previous
                  </Button>
                  <Button
                    size="sm"
                    variant="outlined"
                    disabled={page * pageSize >= totalRows}
                    onClick={() => setPage((value) => value + 1)}
                  >
                    Next
                  </Button>
                  <Button size="sm" onClick={onClose}>
                    Close
                  </Button>
                </Stack>
              </Stack>
            </>
          )
        )}
      </ModalDialog>
    </Modal>
  );
};
